import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PageResponse } from './page-response';
import { CustomsProduct } from './customs-calculation';

const API_URL = '/cargo/api/cargo-requests';

export interface CargoRequestProduct {
  cargoTypeId: number;
  name?: string;
  tnvedCode?: string;
  quantity?: number;
  weightKg?: number;
  isDangerous: boolean;
  isOversized: boolean;
  totalWeightKg: number;
  lengthMm?: number;
  widthMm?: number;
  heightMm?: number;
  volumeM3?: number;
  isNonStackable: boolean;
}

import { CreateCustomsProductRequest } from './customs-calculation';

export interface CreateCargoRequest {
  rateType: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER';
  fromCountryId: number;
  fromCity: string;
  toCountryId: number;
  toCity: string;
  loadingDate: string; // ISO date string
  price: number;
  currencyId: number;
  calculateCustomsPayments: boolean;
  isDraft?: boolean;
  products: CargoRequestProduct[];
  customsProducts?: CreateCustomsProductRequest[];
  /** ID уже существующего независимого расчёта таможенных платежей для привязки (сценарий «Обработать»). */
  customsCalculationId?: number;
}

export interface CargoRequest {
  id: number;
  /** Менеджер вашей компании-экспедитора, ответственный за запрос. */
  forwarderManagerId?: number;
  forwarderManagerName?: string;
  /** Менеджер, за которым закреплён запрос (виден только сотрудникам компании-грузовладельца). */
  assignedManagerId?: number;
  assignedManagerName?: string;
  isDraft?: boolean;
  rateType: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER';
  fromCountryId: number;
  fromCity: string;
  toCountryId: number;
  toCity: string;
  loadingDate: string;
  price: number;
  currencyId: number;
  calculateCustomsPayments: boolean;
  products: CargoRequestProduct[];
  customsProducts?: CustomsProduct[];
  customsCalculationStarted?: boolean;
  customsCalculationStartedByUserId?: number;
  customsCalculationStartedAt?: string;
  customsCalculationCompleted?: boolean;
  customsCalculationCompletedByUserId?: number;
  customsCalculationCompletedAt?: string;
  customsCalculationCompletedWithAi?: boolean;
  createdAt?: string;
  updatedAt?: string;
  bidsCount?: number;
  hasDeal?: boolean;
  /** ID сделки, если она уже создана (принята ставка экспедитора); нет, если сделки ещё нет */
  dealId?: number;
  /** Название компании грузовладельца (для отображения брокеру) */
  cargoOwnerCompanyName?: string;
  /** Название компании экспедитора, чья ставка принята (для отображения брокеру); нет, если сделки ещё нет */
  freightForwarderCompanyName?: string;
}

export interface CargoRequestAutoDeal {
  cargoRequestId: number;
  bidId: number;
  dealId: number;
}

export interface SearchCargoRequestsRequest {
  /** Для экспедитора: запросы, закреплённые за этим менеджером его компании. */
  forwarderAssigneeId?: number;
  forwarderAssigneeUnassigned?: boolean;
  /** Только запросы этого менеджера (для администратора компании). */
  assigneeId?: number;
  /** Только незакреплённые запросы. */
  assigneeUnassigned?: boolean;
  rateType?: 'FTL' | 'LTL' | 'AIR' | 'CONTAINER';
  fromCountryId?: number;
  fromCity?: string;
  toCountryId?: number;
  toCity?: string;
  loadingDateFrom?: string; // ISO date string
  loadingDateTo?: string; // ISO date string
  priceFrom?: number;
  priceTo?: number;
  currencyId?: number;
  calculateCustomsPayments?: boolean;
  status?: 'PENDING' | 'ACCEPTED';
  dateFrom?: string; // ISO date string (YYYY-MM-DD)
  dateTo?: string; // ISO date string (YYYY-MM-DD)
}


@Injectable({
  providedIn: 'root'
})
export class CargoRequestService {
  constructor(
    private http: HttpClient
  ) {}

  getCargoRequests(): Observable<CargoRequest[]> {
    // Interceptor автоматически добавит Authorization заголовок
    return this.http.get<CargoRequest[]>(API_URL);
  }

  getCargoRequestsPaginated(page: number = 0, size: number = 20): Observable<PageResponse<CargoRequest>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    httpParams = httpParams.set('sort', 'id,desc');
    return this.http.get<PageResponse<CargoRequest>>(API_URL, { params: httpParams });
  }

  getCargoRequest(id: number): Observable<CargoRequest> {
    // Interceptor автоматически добавит Authorization заголовок
    return this.http.get<CargoRequest>(`${API_URL}/${id}`);
  }

  createCargoRequest(request: CreateCargoRequest): Observable<CargoRequest> {
    // Interceptor автоматически добавит Authorization заголовок
    return this.http.post<CargoRequest>(API_URL, request);
  }

  createCargoRequestWithSystemForwarder(request: CreateCargoRequest): Observable<CargoRequestAutoDeal> {
    return this.http.post<CargoRequestAutoDeal>(`${API_URL}/with-system-forwarder`, request);
  }

  updateCargoRequest(id: number, request: Partial<CreateCargoRequest>): Observable<CargoRequest> {
    // Interceptor автоматически добавит Authorization заголовок
    return this.http.put<CargoRequest>(`${API_URL}/${id}`, request);
  }

  deleteCargoRequest(id: number): Observable<void> {
    // Interceptor автоматически добавит Authorization заголовок
    return this.http.delete<void>(`${API_URL}/${id}`);
  }

  searchCargoRequests(request: SearchCargoRequestsRequest, page: number = 0, size: number = 20): Observable<PageResponse<CargoRequest>> {
    // Interceptor автоматически добавит Authorization заголовок
    let httpParams = new HttpParams();
    httpParams = httpParams.set('page', page.toString());
    httpParams = httpParams.set('size', size.toString());
    
    return this.http.post<PageResponse<CargoRequest>>(`${API_URL}/search`, request, { params: httpParams });
  }
}
